import {
  startOfDay,
  addDays,
  isBefore,
  isDate,
  endOfDay,
  milliseconds,
  intervalToDuration,
  isSameDay
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

const dateFormat = { month: 'long', day: 'numeric' };
const timeFormat = { hour: 'numeric', minute: 'numeric' };
const dateTimeFormat = {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
};
const dateTimeAndZoneFormat = {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short'
};
const timeAndZoneFormat = {
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short'
};

export const TimeStatusTypes = Object.freeze({
  NOT_STARTED: 'NOT_STARTED',
  DURING: 'DURING',
  ENDED: 'ENDED'
});

export const CALENDAR_DATE_FORMAT = 'eeee, MMMM d, yyyy'; // Format: Friday, July 30, 2021

/**
 * Converts a time into 24 hour format
 * 1:45 PM => 13:45
 * @param timeString value as a string
 */
export const formatTimeTo24Hr = timeString => {
  const hasAM = timeString.match('([Aa]\\.?[Mm])');
  const hasPM = timeString.match('([Pp]\\.?[Mm])');
  if (timeString && (hasAM || hasPM)) {
    // remove AM/PM
    const timeValue = timeString.substring(0, timeString.length - 3);
    const splitTime = timeValue.match(/\d+/g);
    let hour = parseInt(splitTime[0], 10);
    if (hasPM) {
      hour = hour < 12 ? hour + 12 : hour;
    } else {
      hour = hour === 12 ? hour - 12 : hour;
    }
    const hourAsString = hour <= 9 ? `0${hour}` : `${hour}`;

    return `${hourAsString}:${splitTime[1]}`;
  }

  return timeString;
};

/**
 * Generates an array of times and offsets with `intervalMinutes` min intervals.
 * eg: for locale: `en-US`, intervalMinutes: 15
 * [
 *   { label: '12:00 AM', value: 0 },
 *   { label: '12:15 AM', value: 15 },
 *   .....,
 *   { label: '11:45 PM', value: 1425 }
 * ]
 *
 * @param locale function from nx-localization
 * @param intervalMinutes
 * */
const FIFTEEN_MINS = 15;
export const timeIntervalArray = (locale, intervalMinutes) => {
  const timeArray = [];
  const start = startOfDay(new Date());
  const end = endOfDay(new Date());
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric'
  });

  let offset = 0;
  for (
    let date = start;
    date < end;
    date.setMinutes(date.getMinutes() + intervalMinutes)
  ) {
    const timeToDisplay = formatter.format(new Date(date));
    timeArray.push({
      value: offset * FIFTEEN_MINS,
      label: timeToDisplay
    });
    offset++;
  }
  return timeArray;
};

/**
 * Returns an array of times in specified intervals of minutes in en-US and localized time format.
 * eg: for locale: `fr-Fr`, intervalMinutes: 15
 * [
 *   { timeInUSFormat: '12:00 AM', timeToDisplay: '12:00' },
 *   { timeInUSFormat: '12:15 AM', timeToDisplay: '12:15' },
 *   .....,
 *   { timeInUSFormat: '11:45 PM', timeToDisplay: '23:40' }
 * ]
 *
 * @param locale
 * @param intervalMinutes
 */
export const localizedTimeIntervalArray = (locale, intervalMinutes) => {
  const localizedTimeArray = [];
  const start = new Date(1, 1, 1, 0);
  const end = new Date(1, 1, 2, 0);
  const USDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric'
  });
  const localizedFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric'
  });
  for (
    let date = start;
    date < end;
    date.setMinutes(date.getMinutes() + intervalMinutes)
  ) {
    const timeInUSFormat = USDateTimeFormatter.format(new Date(date));
    const timeToDisplay =
      locale !== 'en-US'
        ? localizedFormatter.format(new Date(date))
        : timeInUSFormat;
    localizedTimeArray.push({
      timeInUSFormat,
      timeToDisplay
    });
  }
  return localizedTimeArray;
};

/**
 * Converts a given date to a specific timezone
 */
export const convertTimezone = (date, timezone, locale) => {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString(locale, {
      timeZone: timezone
    })
  );
};

/**
 * Return the start of day for the given date, in the given timezone
 */
export const startOfDayTz = (date, timezone) => {
  if (!timezone) {
    return startOfDay(new Date(date));
  }
  const zonedTime = utcToZonedTime(date, timezone);
  return zonedTimeToUtc(startOfDay(zonedTime), timezone);
};

/**
 * Return the start of next day for the given date, in the given timezone
 */
export const startOfNextDayTZ = (date, timezone) => {
  return addDays(startOfDayTz(date, timezone), 1);
};

/**
 * Return the end of day for the given date, in the given timezone
 */
export const endOfDayTz = (date, timezone) => {
  if (!timezone) {
    return endOfDay(new Date(date));
  }
  const zonedTime = utcToZonedTime(date, timezone);
  return zonedTimeToUtc(endOfDay(zonedTime), timezone);
};

/**
 * Returns the amount of milliseconds before untilDate, and if untilDate
 * is in the past 0 is returned. This is for use when needing to have setTimeout
 * execute at `untilDate`.
 */
export const millisecondsUntilDate = (untilDate, nowDate) => {
  const now = nowDate ? new Date(nowDate) : new Date();
  const until = new Date(untilDate);

  const msDiff = until - now;

  return Math.max(msDiff, 0);
};

/**
 * Generates UTC date from time in format '12:00 AM' and date in format 'Friday, July 30, 2021'
 * */
export const getUtcTime = (time, date, preferredTimezone) => {
  const dateTime = new Date(`${date} ${formatTimeTo24Hr(time)}`);
  return zonedTimeToUtc(dateTime, preferredTimezone);
};

/**
 * @param tz the time zone name received through tz query param
 * @param eventTimeZone the event time zone
 * @returns tz if it is valid, otherwise event time zone
 */
export function getSanitizedTimeZone(tz, eventTimeZone) {
  try {
    // eslint-disable-next-line new-cap
    Intl.DateTimeFormat(undefined, {
      timeZone: tz
    });
    return tz;
  } catch (ex) {
    // invalid time zone present, fallback to event time zone
    return eventTimeZone;
  }
}

/**
 * Check the time status (not started, during, ended) given and start and end time
 *
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
export const checkTimeStatus = (startTime, endTime) => {
  const now = new Date();
  const parsedNowTime = isDate(now) ? now : Date.parse(now);
  const parsedStartTime = isDate(startTime) ? startTime : Date.parse(startTime);
  const parsedEndTime = isDate(endTime) ? endTime : Date.parse(endTime);

  if (isBefore(parsedNowTime, parsedStartTime)) {
    return TimeStatusTypes.NOT_STARTED;
  }
  if (isBefore(parsedNowTime, parsedEndTime)) {
    return TimeStatusTypes.DURING;
  }
  return TimeStatusTypes.ENDED;
};

/**
 * Returns date and time in format 'May 22, 3:00 PM - 3:30 PM GMT+1' if end time is provided
 * otherwise return date and start time in format 'May 22, 3:00 PM GMT+1'
 *
 * @param date
 * @param start
 * @param end
 * @returns
 */
export const formatDateTime = (date, start, end) => {
  if (end) {
    const startTime = date(start, dateTimeFormat);
    const endTime = date(end, timeAndZoneFormat);
    return `${startTime} - ${endTime}`;
  }
  return date(start, dateTimeAndZoneFormat);
};

/**
 * Returns date in format 'May 22'
 *
 * @param date
 * @param startDate
 * @returns
 */
export const formatDate = (date, startDate) => {
  return date(startDate, dateFormat);
};

/**
 * Returns time in format '3:00 PM - 3:30 PM GMT+1' if end time is provided
 * otherwise return start time in format '3:00 PM GMT+1'
 *
 * @param date
 * @param start
 * @param end
 * @returns
 */
export const formatTime = (date, start, end) => {
  if (end) {
    const startTime = date(start, timeFormat);
    const endTime = date(end, timeAndZoneFormat);
    return `${startTime} - ${endTime}`;
  }
  return date(start, timeAndZoneFormat);
};

/**
 * Returns start of day in UTC
 * @param date
 * @param timezone
 * @returns
 */
export const getStartDateUtc = (date, timezone) => {
  return zonedTimeToUtc(startOfDay(new Date(date)), timezone).toISOString();
};

/**
 * Returns end of day in UTC
 * @param date
 * @param timezone
 * @returns
 */
export const getEndDateUtc = (date, timezone) => {
  return zonedTimeToUtc(endOfDay(new Date(date)), timezone).toISOString();
};

/**
 * Get duration between start and end time in minutes
 */
export const getDurationInMinutes = (
  isoFormattedStartTime,
  isoFormattedEndTime
) => {
  if (
    !isoFormattedStartTime ||
    !isoFormattedEndTime ||
    isoFormattedStartTime >= isoFormattedEndTime
  ) {
    return null;
  }

  const durationInMilliSeconds = milliseconds(
    intervalToDuration({
      start: new Date(isoFormattedStartTime),
      end: new Date(isoFormattedEndTime)
    })
  );

  return durationInMilliSeconds / 60000;
};

export const getLocalDate = dateString => {
  const dateInTimeZone = new Date(dateString);
  const year = dateInTimeZone.getFullYear();
  const month = (dateInTimeZone.getMonth() + 1).toString().padStart(2, '0');
  const day = dateInTimeZone.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const isSameDayTz = (currentDate, dateToCompare, timezone) => {
  return isSameDay(
    startOfDayTz(currentDate, timezone),
    startOfDayTz(dateToCompare, timezone)
  );
};
